<template>
  <div id="academic_faculty">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc"
      :color="this.page.color"></usc-hero>
    <usc-intro :copy="this.intro">
    </usc-intro>
    <div class="body gutter-lg mb">
      <p><i>Marshall is home to some of the nation’s most sought-after researchers and educators. Our tenured faculty include an advisor to five U.S. presidents, a former chief economist of the Securities and Exchange Commission and a renowned scholar who recently received the most coveted honor in marketing for an article recognized as having made the best contribution to the science of marketing in the past decade.</i></p>
      <p>At Marshall, close faculty-student partnerships are the norm. Over the course of their time here, our undergraduates get to know their professors as teachers, mentors and friends. It’s collaborative learning at its finest, creating incredible opportunities for personal development, professional growth and groundbreaking discovery.</p>
      <p>Outside of the classroom, faculty also serve as student-organization advisors and case coaches, as well as travel on service-learning and international educational trips.</p>
    </div>
    <usc-calloutLink urltext="Meet your future mentors" url="https://www.marshall.usc.edu/faculty-research/faculty-directory"></usc-calloutLink>
    <div class="body gutter-lg mb">
      <p>Learn about the <a class="inline-hyperlink" href="https://www.marshall.usc.edu/faculty-research/research-usc-marshall">Marshall faculty’s latest and most exciting research</a>.</p>
      <usc-videoEmbed :frameTitle="this.video.frameTitle" :caption="this.video.caption" :videoSource="this.video.src"></usc-videoEmbed>
      <img src="../../../static/ADV-faculty-24.png"
        alt="240 Full-Time Faculty, 39 Endowed Chairs, and 7 Centers of Excellence."
        title="240 Full-Time Faculty, 39 Endowed Chairs, and 7 Centers of Excellence." style="width: 100%;" />
    </div>
    <div class="body gutter-lg mb">
      <p><strong>Student-Faculty Partnerships</strong><br /><i>Marshall students have the opportunity to work one-on-one
          with faculty in research and supplemental-learning opportunities.</i></p>
      <p><strong>BUAD 490: Directed Research.</strong> Driven students may approach a faculty member of their choosing
        to discuss possible research topics and become a “student colleague” with that faculty member on a research
        project. Offering up to eight units, the course can be spread over multiple semesters. Students will work with
        the faculty member to design the project, develop a meeting schedule, identify course deliverables and conduct
        the research throughout the course of the project.</p>
      <p><strong>BUAD 499: Special Topics.</strong> Another student-driven course, BUAD 499 allows students to work with
        faculty to gain additional expertise on a subject of the student’s choosing (for instance, how business
        intertwines with the student’s minor). Students will discuss their plan and expectations with a faculty member
        of their choosing, who will then work with the student to design a learning plan, coursework and deliverables,
        then meet with the student throughout the course to discuss topics and gauge progress. This class can also be
        spread over semesters with up to eight units possible.</p>
      <p><strong>Research</strong><br />Marshall’s research reputation is strong and growing. Faculty members have
        expertise across all major business disciplines. They publish consistently in prominent professional journals
        and hold editorial positions in leading journals. In addition to their broad-ranging interests, our faculty
        share a common bond—they are drawn to Marshall by a unique community that presents opportunities for
        professional growth, groundbreaking discovery and sharing the lessons they learn from their research with
        students.</p>
      <p><strong>Centers for Excellence</strong><br />Our institutes, Centers of Excellence and inititives are another area where our faculty’s knowledge and experience provide students with unique opportunities beyond the classroom.<br />They include:</p>
      <ul>
        <li>Peter Arkley Institute for Risk Management</li>
        <li>Brittingham Social Enterprise Lab</li>
        <li>Randall R. Kendrick Global Supply Chain Institute</li>
        <li>Institute for Outlier Research in Business</li>
        <li>Center for Effective Organizations</li>
        <li>Center for Global Innovation</li>
        <li>Center for Investment Studies</li>
        <li>Lloyd Greif Center for Entrepreneurial Studies</li>
        <li>Neely Center for Ethical Leadership and Decision Making</li>
        <li>Initiative on Digital Competition</li>
        <li>Vaneck Digital Assets Initiative</li>
      </ul>
    </div>
    <div class="body mb gutter-lg">
      <div class="cardinal-line mt" style="margin-bottom: 1em;"></div>
    </div>
    <div class="body gutter-lg mb">
      <h2>Thought Leaders</h2>
      <p><i>Distinguished researchers fill the halls in every department at Marshall. Some of the thought leaders with
          whom you will work and learn include the following:</i></p>
    </div>
    <div class="body gutter-lg mb">
      <h3>Communication: Robyn Walker</h3>
      <p>Expertise:
        Communication and decision making in teams</p>
      <p>Select publications:
        American Communication Journal; International Journal of Diversity in Organizations, Communities and Nations;
        International Journal of Knowledge, Culture and Change Management; and Business Communication Quarterly</p>
      <p>
        Editor, Journal of Business Communication; Mellon Fellow, USC; Peer Achievement Faculty Award, USC Student
        Senate Leadership Council; three-time recipient of both the USC Marshall School of Business Summer Research
        Grant and USC Undergraduate Research Grant</p>
    </div>
    <div class="body gutter-lg mb">
      <h3>Economics: Kevin Murphy</h3>
      <p>Expertise:
        Executive compensation</p>
      <p>Select publications:
        Journal of Accounting & Economics, Journal of Financial Economics, American Economic Review Papers and
        Proceedings and the University of Chicago Law Review</p>
      <p>Select honors/experience: Associate Editor, Journal of Financial Economics and Journal of Corporate Finance
        (current); expert, U.S. Department of Treasury, Office of the Special Master for Executive Compensation (2009);
        Chairman, Academic Research Committee, American Compensation Association (1997-99)</p>
    </div>
    <div class="body gutter-lg mb">
      <h3>Marketing: Valerie Folkes</h3>
      <p>Expertise:
        Consumers’ responses to negative information and attributions for service and product performance</p>
      <p>Select publications:
        Journal of Consumer Research, Psychology and Marketing and Journal of the Academy of Marketing Science</p>
      <p>Select honors:
        Best Article on Services Award, American Marketing Association Services SIG; USC Mellon Award for Excellence in
        Marketing; Outstanding Reviewer Award, Journal of Consumer Research; President, Association for Consumer
        Research (2001)</p>
    </div>
    <div class="body gutter-lg mb">
      <h3>Organizational Behavior: Nathanael Fast</h3>
      <p>Expertise:
        The influence of power on people and their leadership effectiveness; the facilitation of the spread of culture
      </p>
      <p>Select publications:
        Journal of Experimental Social Psychology and Psychological Science</p>
      <p>Select honors:
        Best Papers Proceedings, Academy of Management; State Farm Doctoral Dissertation Award; All-American Scholar
        Athlete, Baseball; Graduate Dean’s Medal, College of Science and Mathematics, CSU-Fresno; Poets & Quants “best
        40 B-school profs under the age of 40”</p>
    </div>
    <div class="body gutter-lg mb">
      <h3>Statistics: Yingying Fan</h3>
      <p>Expertise:
        High-dimensional statistical inference, classification and variable selection, nonparametric statistics and
        financial econometrics</p>
      <p>Select publications:
        The Annals of Statistics, Journal of Econometrics and Journal of the American Statistical Association</p>
      <p>Select honors:
        National Science Foundation Grant; Fellowship, Princeton University; Chair, IOM Department Statistics Seminar,
        USC Marshall School of Business (current); Zumberge Individual Award, USC James H. Zumberge Faculty Research and
        Innovation Fund</p>
    </div>
    <div class="body gutter-lg mb">
      <h3>Strategy: Peer Fiss</h3>
      <p>Expertise:
        Diffusion of practices, framing and decoupling, symbolic management, corporate governance, strategic management,
        configurational theory, fuzzy set qualitative comparative analysis (fsQCA), set-theoretic methods in management
        and the social sciences</p>
      <p>Select publications:
        Academy of Management Journal, Academy of Management Review and Strategic Management Journal</p>
      <p>Select honors:
        Ascendant Scholar Award, Western Academy of Management; Award for Excellence in Research, USC Marshall School of
        Business; Fellow, Judge Business School, University of Cambridge</p>
    </div>
  </div>
</template>

<script>
  import hero from '@/components/Hero'
  import intro from '@/components/Intro'
  import Cta from '@/components/Cta'
  import CalloutLink from '@/components/CalloutLink'
  import videoEmbed from '@/components/videoEmbed'

  export default {
    name: 'UndergradAccounting',
    components: {
      'usc-hero': hero,
      'usc-intro': intro,
      'usc-cta': Cta,
      'usc-videoEmbed': videoEmbed,
      'usc-calloutLink': CalloutLink
    },
    data () {
      return {
        page: {
          color: '#ffcc00'
        },
        hero: {
          title: 'Faculty',
          loResSrc: '../../../static/HeroImages/Advantages-Marshall2.jpg',
          hiResSrc: '../../../static/HeroImages/Advantages-Marshall2.jpg'
        },
        intro: [
          'A leading business school is founded on its thought leadership and its commitment to producing information and ideas that have the potential to change the world. Leaders in all professions want to be close to the source of those ideas, many of which emanate from Marshall.'
        ],
        video: {
          caption: 'AJ Ogilvie, Assistant Professor of Clinical Business Communication - The Marshall Difference: Human-Centric Problem-Solving',
          src: 'https://player.vimeo.com/video/642795234?title=0&byline=0&portrait=0',
          frameTitle: 'AJ Ogilvie, Assistant Professor of Clinical Business Communication - The Marshall Difference: Human-Centric Problem-Solving'
        },
        CalloutLink: {
          title: 'Meet your future mentors',
          urltext: 'Meet your future mentors',
          url: 'https://www.marshall.usc.edu/faculty-research/faculty-directory'
        }
      }
    },
    created () {
      this.$root.setPageHeader('Academics', 'Faculty')
    }
  }
</script>

<style scoped lang="scss">
  .cardinal-line.mt {
    background: #000;
    height: 2px;
    margin-top: 60px;
  }

  ul {
    margin-left: 1em;
  }

  li {
    margin-left: 1em;
    padding-left: 1em;
    list-style-type: disc;
  }

</style>



// WEBPACK FOOTER //
// src/pages/advantages/Faculty.vue